/**
 * Audit log helper (US-026 / US-031.4).
 *
 * Writes a row to `AuditLog` from any router handler. Failures are
 * swallowed and logged to stderr — an audit-log write should never
 * crash the user-facing operation that produced it.
 *
 * IMPORTANT: this module is imported transitively by every router file
 * via the router barrel. A top-level `import { prisma } from '@app/db'`
 * here pulls @prisma/adapter-neon + ws + Node built-ins into the client
 * bundle (the router types are pulled into apps/www at build time for
 * oRPC's client inference). The dynamic-import inside writeAuditLog
 * keeps the side-effecting `@app/db` import on the server-only code
 * path; symptom of the alternative was client console errors like
 * `Loading the script 'node:events' violates CSP` and a dead Google
 * sign-in button on every preview that imported a router using audit.
 */

export interface AuditWriteInput {
  action: string;
  category: string;
  actor: { id: string; name?: string | null; email?: string | null; role?: string | null };
  target?: { type: string; id: string; name?: string | null };
  context?: Record<string, unknown>;
  success?: boolean;
  errorMessage?: string;
  ip?: string | null;
}

export async function writeAuditLog(input: AuditWriteInput): Promise<void> {
  try {
    const { prisma } = await import('@app/db');
    await prisma.auditLog.create({
      data: {
        action: input.action,
        category: input.category,
        actorId: input.actor.id,
        actorName: input.actor.name ?? null,
        actorEmail: input.actor.email ?? null,
        actorRole: input.actor.role ?? null,
        actorIp: input.ip ?? null,
        targetType: input.target?.type ?? null,
        targetId: input.target?.id ?? null,
        targetName: input.target?.name ?? null,
        context: input.context ? JSON.stringify(input.context) : null,
        success: input.success ?? true,
        errorMessage: input.errorMessage ?? null,
      },
    });
  } catch (e) {
    // Never let an audit write break a feature path.
    console.error('audit write failed:', e instanceof Error ? e.message : String(e));
  }
}
